<template>
    <div>
        <div class="flex flex-col gap-2 mb-3 lg:flex-row lg:mb-6">
            <div class="lg:w-60">
                <vx-label>Key</vx-label>
                <vx-selectable
                    size="lg"
                    :options="options"
                    :loading="searching"
                    :taggable="true"
                    @search="search"
                    v-model="key"
                    @option:selected="focusValue()"
                    :error="displayFirstError(validate.key)"
                >
                    <template v-slot:no-options> Search or create a custom attribute key. </template>
                </vx-selectable>
            </div>
            <div class="flex grow gap-2">
                <div class="grow">
                    <vx-label>Value</vx-label>
                    <vx-input
                        ref="valueEl"
                        size="lg"
                        v-model="value"
                        @keypress.enter="add()"
                        :error="displayFirstError(validate.value)"
                    ></vx-input>
                </div>
                <div class="pt-7 lg:pt-6">
                    <vx-button
                        @click="add()"
                        data-test="custom-attribute-add"
                        aria-label="Add custom attribute"
                        size="lg"
                        type="button"
                    >
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>
        <vx-alert :closeable="false" v-if="!customAttributes.length">
            No custom attributes were added to this contact.
        </vx-alert>
        <div v-else>
            <vx-list-animated v-model="customAttributes" value="key" label="key">
                <template v-slot:item="{ item }">
                    <div
                        class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2"
                    >
                        <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.key }}</div>
                        <div class="line-clamp-3 break-all">{{ item.value }}</div>
                    </div>
                </template>
            </vx-list-animated>
        </div>
    </div>
</template>

<script setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxInput, VxLabel, VxListAnimated, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useFormSubmissionChild } from '~/composables/useFormSubmission';
import { customAttributesClient } from '~/services';
import debounce from '~/utils/debounce';
import { csvInjection, customAttributeKey, customAttributeValue, displayFirstError } from '~/utils/validation';

const teamId = Spark.state.currentTeam.id;

const customAttributes = defineModel({ type: Array, required: true });

const valueEl = ref();
const focusValue = async () => {
    await nextTick();
    valueEl.value.field?.focus();
};

const key = ref('');
const value = ref('');

const options = ref([]);
const searching = ref(false);
const adding = ref(false);

const { onBeforeSubmit, offBeforeSubmit } = useFormSubmissionChild();

const search = debounce((search) => {
    searching.value = true;

    customAttributesClient
        .customAttributesAutocomplete(teamId, {
            query: search,
        })
        .then((response) => {
            options.value = response.data.data;
            searching.value = false;
        });
}, 200);

const validate = useVuelidate(
    {
        key: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(80),
            format: customAttributeKey,
            csvInjection: csvInjection,
        },
        value: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(350),
            format: customAttributeValue,
            csvInjection: csvInjection,
        },
    },
    {
        key,
        value,
    }
);

const submitPendingAttribute = async () => {
    if (adding.value) {
        return;
    }

    if (!key.value && !value.value) {
        return;
    }

    await add();
};

const add = async () => {
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();

    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    const normalizedKey = key.value.toLowerCase();

    customAttributes.value = [
        { key: normalizedKey, value: value.value },
        ...customAttributes.value.filter((customAttribute) => customAttribute.key.toLowerCase() !== normalizedKey),
    ];

    key.value = '';
    value.value = '';
    adding.value = false;
};

onMounted(() => {
    onBeforeSubmit(submitPendingAttribute);
});

onUnmounted(() => {
    offBeforeSubmit(submitPendingAttribute);
});
</script>
